import { Constants } from '../config/Constants.js';
import { getT } from '../i18n/index.js';
import { getLoginPromptHiddenFromUrl } from '../utils/urlParams.js';
import { requestLoginPrompt, dismissLoginPrompt } from './react/loginPromptBridge';

/**
 * @file loginPrompt.js
 * @description Login-nudge entry for the auth/settings/MIDI flows. Tier-1 migration: the prompt is now
 * the React `LoginPrompt` region (rendered in the HeaderBar). This file is just the imperative bridge —
 * it computes the href/label and forwards to the React sink. No DOM, no positioning, no listeners.
 */

function sanitizeBaseUrl(base) {
  if (!base || typeof base !== 'string') return '';
  return base.replace(/\/+$/, '');
}

/**
 * The root app's login page.
 *
 * `returnTo` is where the visitor should land once they are signed in, as an ABSOLUTE url. It rides
 * on `?redirectTo=`, which is the root app's own existing convention (its middleware and login form
 * both read that param) — the orbiter origin is allowlisted there by exact match, so a value from
 * anywhere else is simply dropped rather than followed. Callers that just want the login page pass
 * nothing and get exactly today's href.
 */
export function getLoginHref(customHref = null, { returnTo = null } = {}) {
  if (typeof customHref === 'string' && customHref.trim().length) {
    return customHref.trim();
  }

  const candidates = [
    Constants?.ROOT_BASE,
    typeof window !== 'undefined' ? window.ROOT_BASE : undefined,
    typeof window !== 'undefined' ? window.__ROOT_BASE__ : undefined,
    typeof window !== 'undefined' ? window.location?.origin : undefined,
  ];

  const base = candidates.find((value) => typeof value === 'string' && value.trim().length) || '';
  const href = `${sanitizeBaseUrl(base)}/auth/login`;
  if (typeof returnTo !== 'string' || !returnTo.trim().length) return href;
  return `${href}?redirectTo=${encodeURIComponent(returnTo.trim())}`;
}

export function ensureLoginPrompt({ text = null, href = null } = {}) {
  if (getLoginPromptHiddenFromUrl()) {
    dismissLoginPrompt();
    return null;
  }

  const t = getT();
  const resolvedText =
    typeof text === 'string' && text.trim().length ? text : t('login.cta');

  // Buffered: if the auth flow fires before the React shell mounts, the request is replayed
  // when the LoginPrompt region registers its sink.
  requestLoginPrompt({
    text: resolvedText,
    href: getLoginHref(href),
    ariaLabel: t('login.ariaLabel'),
  });
  return null;
}

export function removeLoginPrompt() {
  dismissLoginPrompt();
}
